import type { Hearing } from "@/lib/public-hearings/types";

import { DistrictCode } from "../DistrictCode";
import { derivePolicyNote } from "../HearingsList/derive-policy-note";
import { deriveZoneTone } from "../HearingsList/derive-zone-tone";

// Promoted zoning-transform panel for the detail surface. Same data
// grammar as the row's `.ph-zone` block — district codes on each
// side of a from→to relationship — but laid out full-width with a
// real arrow glyph between the two sides, larger code chips, and a
// scope-only variant for statutory plan amendments. The tone token
// (`upzone` / `dc` / `policy`) drives the same accent treatment as
// the row, so a hearing reads with the same colour in both surfaces.
export function LandUseChange({ hearing: h }: { hearing: Hearing }) {
  const tone = deriveZoneTone(h);
  const isPolicy = tone === "policy";
  const policyNote = derivePolicyNote(h);

  if (isPolicy && !policyNote && h.fromDistricts.length === 0 && h.toDistricts.length === 0) {
    return null;
  }

  return (
    <section className="ph-detail-panel" aria-label="Land-use change">
      <h2 className="ph-detail-panel-l">
        <span>Land-use change</span>
        <span className="count" aria-hidden>
          {isPolicy ? "policy" : tone}
        </span>
      </h2>
      <div className="ph-detail-zone" data-tone={tone}>
        {isPolicy ? (
          <div className="ph-detail-zone-scope">
            <span className="l">Scope</span>
            <span className="v">Policy only — no district change</span>
          </div>
        ) : (
          <div className="ph-detail-zone-flow">
            <div className="ph-detail-zone-side">
              <span className="l">From</span>
              <div className="ph-detail-zone-codes">
                {h.fromDistricts.length > 0 ? (
                  h.fromDistricts.map((d) => <DistrictCode key={`from-${d}`} code={d} />)
                ) : (
                  <code>—</code>
                )}
              </div>
            </div>
            <span className="ph-detail-zone-arrow" aria-hidden>
              <svg viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 14h17" />
                <path d="M16 8l6 6-6 6" />
              </svg>
            </span>
            <div className="ph-detail-zone-side">
              <span className="l">To</span>
              <div className="ph-detail-zone-codes">
                {h.toDistricts.length > 0 ? (
                  h.toDistricts.map((d) => <DistrictCode key={`to-${d}`} code={d} />)
                ) : (
                  <code>—</code>
                )}
              </div>
            </div>
          </div>
        )}
        {policyNote && !isPolicy && (
          <p className="ph-detail-zone-policy">{policyNote}</p>
        )}
      </div>
    </section>
  );
}
